// static/table_engine_src/plugins/bucketPopoverPlugin.js
"use strict";

/*
  Generic Bucket Popover Plugin for TableEngine

  Opt-in via spec.bucketPopover:
    bucketPopover: {
      showOnCols: ["vega"],
      vectorKeyByCol: { vega: "sigma_sens" },
    }

  Row contract:
    tr.__bucketVectors = { sigma_sens: [...], ... }
    tr.__scenarioMeta / tr.__riskMeta optional for subtitle.
*/

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function cssEsc(s) {
  try { return CSS.escape(String(s)); }
  catch { return String(s).replace(/["\\]/g, "\\$&"); }
}

function ensureHeaderLabelSpan(th) {
  if (!th) return null;

  const existing = th.querySelector(":scope > .te-col-label");
  if (existing) return existing;

  const span = document.createElement("span");
  span.className = "te-col-label";
  while (th.firstChild) span.appendChild(th.firstChild);
  th.appendChild(span);
  return span;
}

// ---------------- Clipboard ----------------
async function copyTextToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(text ?? ""));
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = String(text ?? "");
    ta.setAttribute("readonly", "true");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

// ---------------- Button feedback (icon swap) ----------------
function svgIcon(name) {
  if (name === "copy") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
    `.trim();
  }
  if (name === "code") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M16 18l6-6-6-6"></path>
        <path d="M8 6l-6 6 6 6"></path>
        <path d="M14 4l-4 16"></path>
      </svg>
    `.trim();
  }
  if (name === "check") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6L9 17l-5-5"></path>
      </svg>
    `.trim();
  }
  if (name === "x") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 6L6 18"></path>
        <path d="M6 6l12 12"></path>
      </svg>
    `.trim();
  }
  return "";
}

function flashIcon(btn, ok) {
  if (!btn) return;
  if (!btn.__teOrigIcon) btn.__teOrigIcon = btn.innerHTML;

  btn.innerHTML = ok ? svgIcon("check") : svgIcon("x");
  btn.disabled = true;
  btn.style.opacity = "0.85";

  clearTimeout(btn.__teFlashT);
  btn.__teFlashT = setTimeout(() => {
    btn.innerHTML = btn.__teOrigIcon;
    btn.disabled = false;
    btn.style.opacity = "";
  }, ok ? 850 : 1200);
}

// ---------------- Tooltip (hover values) ----------------
function getChartTooltipEl() {
  let el = document.querySelector(".ui-bucket-tip");
  if (el) return el;

  el = document.createElement("div");
  el.className = "ui-bucket-tip";
  el.style.position = "fixed";
  el.style.left = "0px";
  el.style.top = "0px";
  el.style.pointerEvents = "none";
  el.style.zIndex = "100005";
  el.style.padding = "6px 8px";
  el.style.borderRadius = "10px";
  el.style.border = "1px solid rgba(255,255,255,0.16)";
  el.style.background = "rgba(10, 18, 32, 0.96)";
  el.style.color = "rgba(255,255,255,0.92)";
  el.style.font = "12px ui-monospace, Menlo, Monaco, Consolas, 'Courier New', monospace";
  el.style.boxShadow = "0 10px 24px rgba(0,0,0,0.35)";
  el.style.display = "none";
  document.body.appendChild(el);
  return el;
}

function showChartTip(text, clientX, clientY) {
  if (!text) return;
  const tip = getChartTooltipEl();
  tip.textContent = String(text);

  tip.style.display = "block";
  tip.style.visibility = "hidden";

  const pad = 10;
  const w = tip.offsetWidth || 180;
  const h = tip.offsetHeight || 32;

  const x = clamp(clientX + 14, pad, window.innerWidth - pad - w);
  const y = clamp(clientY + 14, pad, window.innerHeight - pad - h);

  tip.style.left = `${Math.round(x)}px`;
  tip.style.top = `${Math.round(y)}px`;
  tip.style.visibility = "visible";
}

function hideChartTip() {
  const tip = document.querySelector(".ui-bucket-tip");
  if (tip) tip.style.display = "none";
}

// ---------------- Colors ----------------
function lerp(a, b, t) { return a + (b - a) * t; }

function colorDiverging(v01) {
  if (v01 <= 0.5) {
    const t = v01 / 0.5;
    const r = lerp(60, 255, t), g = lerp(120, 255, t), b = 255;
    return `rgb(${r | 0},${g | 0},${b | 0})`;
  } else {
    const t = (v01 - 0.5) / 0.5;
    const r = 255, g = lerp(255, 170, t), b = lerp(255, 80, t);
    return `rgb(${r | 0},${g | 0},${b | 0})`;
  }
}

// ---------------- UI styles (chart area) ----------------
function ensureBucketStyles() {
  if (document.getElementById("__te_bucket_popover_styles")) return;

  const st = document.createElement("style");
  st.id = "__te_bucket_popover_styles";
  st.textContent = `
    .ui-bucket-subline{
      margin: 2px 0 8px;
      font-size: 12px;
      color: var(--ui-muted, rgba(200,225,255,0.80));
      opacity: 0.95;
    }
    .ui-bucket-summary{
      margin: 0 0 10px;
      font-size: 12px;
      color: rgba(220,235,255,0.82);
      opacity: 0.95;
    }
    .ui-bucket-canvas{
      width: 520px;
      max-width: 100%;
      height: 180px;
      display:block;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.10);
      background: rgba(255,255,255,0.03);
      box-shadow: 0 10px 24px rgba(0,0,0,0.18) inset;
    }
    .ui-bucket-foot{
      margin-top: 10px;
      font-size: 12px;
      color: rgba(220,235,255,0.78);
      opacity: 0.95;
    }
  `;
  document.head.appendChild(st);
}

// ---------------- Popover (UiPopovers) ----------------
const state = {
  pop: null,
  anchor: null,
  tr: null,
  lastVectorKey: null,
  lastColKey: null,
  canvas: null,
  subEl: null,
  summaryEl: null,
  btnCsv: null,
  btnJson: null,
};

function setPopoverTitle(pop, text) {
  const t = pop?.el?.querySelector?.(".ui-pop-title");
  if (t) t.textContent = String(text || "Buckets");
}

function ensurePopover() {
  if (state.pop) return state.pop;

  ensureBucketStyles();

  if (!window.UiPopovers?.create) {
    console.error("BucketPopoverPlugin: UiPopovers not available (ui/popovers.js not loaded in bundle).");
    return null;
  }

  const bodyHTML = `
    <div class="ui-bucket-subline" data-ui-bucket-sub="1"></div>
    <div class="ui-bucket-summary" data-ui-bucket-summary="1"></div>
    <canvas class="ui-bucket-canvas" width="520" height="180"></canvas>
    <div class="ui-bucket-foot">Tip: hover bars to see exact values. Click outside (or press Esc) to close.</div>

    <div class="ui-cmdbar" style="padding-top:12px;">
      <button type="button" class="ui-cmd-btn" data-ui-bucket-csv="1" title="Copy CSV" aria-label="Copy CSV">
        ${svgIcon("copy")}
      </button>
      <button type="button" class="ui-cmd-btn" data-ui-bucket-json="1" title="Copy JSON" aria-label="Copy JSON">
        ${svgIcon("code")}
      </button>
    </div>
  `.trim();

  const pop = window.UiPopovers.create({
    title: "Buckets",
    bodyHTML,
    className: "ui-bucket-pop",
  });

  state.pop = pop;
  state.canvas = pop.el.querySelector("canvas.ui-bucket-canvas");
  state.subEl = pop.el.querySelector('[data-ui-bucket-sub="1"]');
  state.summaryEl = pop.el.querySelector('[data-ui-bucket-summary="1"]');
  state.btnCsv = pop.el.querySelector('[data-ui-bucket-csv="1"]');
  state.btnJson = pop.el.querySelector('[data-ui-bucket-json="1"]');

  if (state.canvas) installBarHover(state.canvas);

  const baseClose = pop.close;
  pop.close = () => {
    baseClose();
    hideChartTip();
    state.anchor = null;
    state.tr = null;
    state.lastVectorKey = null;
    state.lastColKey = null;
  };

  return pop;
}

function closePopover() {
  if (state.pop) state.pop.close();
}

// ---------------- Chart draw ----------------
function drawBars(canvas, values, labels) {
  const ctx = canvas?.getContext?.("2d");
  if (!ctx) return;

  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  const vals = Array.isArray(values) ? values.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : [];
  const n = vals.length;

  if (!n) {
    ctx.fillStyle = "rgba(235,245,255,0.92)";
    ctx.font = "14px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    ctx.fillText("No buckets for this cell.", 18, 36);
    canvas.__teBarsState = null;
    return;
  }

  const mL = 48, mR = 18, mT = 18, mB = 46;
  const plotW = W - mL - mR;
  const plotH = H - mT - mB;

  const maxAbs = Math.max(...vals.map((v) => Math.abs(v))) || 1.0;

  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(mL, mT);
  ctx.lineTo(mL, mT + plotH);
  ctx.lineTo(mL + plotW, mT + plotH);
  ctx.stroke();

  const y0 = mT + plotH / 2;
  ctx.strokeStyle = "rgba(255,255,255,0.14)";
  ctx.beginPath();
  ctx.moveTo(mL, y0);
  ctx.lineTo(mL + plotW, y0);
  ctx.stroke();

  const barW = plotW / n;
  const padX = Math.max(1, barW * 0.16);

  for (let i = 0; i < n; i++) {
    const v = vals[i];
    const a = Math.min(1, Math.abs(v) / maxAbs);
    const h = a * (plotH / 2 - 6);

    const x = mL + i * barW + padX / 2;
    const w = Math.max(1, barW - padX);
    const y = v >= 0 ? y0 - h : y0;

    const signed = v >= 0 ? a : -a;
    const v01 = (signed + 1) * 0.5;

    ctx.fillStyle = colorDiverging(v01);
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = "rgba(0,0,0,0.28)";
    ctx.strokeRect(x, y, w, h);
  }

  const lab =
    Array.isArray(labels) && labels.length === n ? labels : Array.from({ length: n }, (_, i) => String(i));

  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.font = "11px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.textAlign = "center";

  const every = n > 24 ? 4 : n > 16 ? 2 : 1;
  for (let i = 0; i < n; i += every) {
    const x = mL + i * barW + barW / 2;
    ctx.fillText(String(lab[i]), x, mT + plotH + 18);
  }

  canvas.__teBarsState = { values: vals, labels: lab, geom: { mL, mT, plotW, plotH, barW } };
}

function installBarHover(canvas) {
  if (!canvas || canvas.__teBarHoverInstalled) return;
  canvas.__teBarHoverInstalled = true;

  const onMove = (e) => {
    const st = canvas.__teBarsState;
    if (!st || !st.geom) return;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);

    const { mL, mT, plotW, plotH, barW } = st.geom;

    if (mx < mL || mx > mL + plotW || my < mT || my > mT + plotH) { hideChartTip(); return; }

    const i = Math.floor((mx - mL) / barW);
    if (!Number.isFinite(i) || i < 0 || i >= st.values.length) { hideChartTip(); return; }

    const lab = st.labels?.[i] ?? String(i);
    const v = st.values[i];
    showChartTip(`${lab}: ${Number(v).toFixed(8)}`, e.clientX, e.clientY);
  };

  canvas.addEventListener("mousemove", onMove, { passive: true });
  canvas.addEventListener("mouseleave", () => hideChartTip(), { passive: true });
}

// ---------------- Data plumbing ----------------
function getColKeyFromCell(cell) {
  if (!cell) return "";
  const ds = cell.dataset || {};
  return ds.colKey || ds.key || ds.out || cell.getAttribute?.("data-col-key") || cell.getAttribute?.("data-key") || cell.getAttribute?.("data-out") || "";
}

function getVectorFor(tr, key) {
  if (!tr || !key) return null;

  const m = tr.__bucketVectors;
  if (m && Array.isArray(m[key]) && m[key].length) return m[key];

  if (key === "sigma_sens" && Array.isArray(tr.__sigmaSens) && tr.__sigmaSens.length) return tr.__sigmaSens;
  if (key === "vega" && Array.isArray(tr.__vegaBuckets) && tr.__vegaBuckets.length) return tr.__vegaBuckets;

  return null;
}

function getLabelsFor(key, n) {
  const pick =
    key === "sigma_sens"
      ? window.__scenarioSigmaBucketLabels || window.__sigmaBucketLabels
      : key === "vega"
      ? window.__vegaBucketLabels || window.__scenarioVegaBucketLabels
      : null;

  if (Array.isArray(pick) && pick.length >= n) return pick.slice(0, n);
  return Array.from({ length: n }, (_, i) => String(i));
}

function buildSubtitleFromMeta(tr) {
  const meta = tr.__scenarioMeta || tr.__riskMeta || tr.__meta || {};
  const parts = [];
  if (meta.identifier) parts.push(`Bond: ${meta.identifier}`);
  if (meta.scenario_idx != null) parts.push(`Scen: ${meta.scenario_idx}`);
  if (meta.rate_level_pct != null) parts.push(`Shift: ${Number(meta.rate_level_pct).toFixed(2)}%`);
  return parts.join("  •  ");
}

function buildSummary(vec) {
  try {
    let sum = 0;
    let maxAbs = 0;
    for (let i = 0; i < vec.length; i++) {
      const v = Number(vec[i]);
      if (!Number.isFinite(v)) continue;
      sum += v;
      const av = Math.abs(v);
      if (av > maxAbs) maxAbs = av;
    }
    const fmt = (x) => {
      const ax = Math.abs(x);
      if (ax >= 1000) return x.toFixed(0);
      if (ax >= 100) return x.toFixed(2);
      if (ax >= 1) return x.toFixed(6);
      return x.toFixed(8);
    };
    return `Σ ${fmt(sum)}   •   max|.| ${fmt(maxAbs)}`;
  } catch {
    return "";
  }
}

function openPopover(anchorEl, tr, vectorKey, clickedColKey) {
  const vec = getVectorFor(tr, vectorKey);
  if (!Array.isArray(vec) || !vec.length) return;

  const pop = ensurePopover();
  if (!pop || !state.canvas) return;

  const titleText =
    vectorKey === "sigma_sens"
      ? clickedColKey === "vega"
        ? "Vega buckets"
        : "σ buckets (dP/dσ)"
      : vectorKey === "vega"
      ? "ν buckets (Vega)"
      : `${vectorKey} buckets`;

  setPopoverTitle(pop, titleText);

  const subtitle = buildSubtitleFromMeta(tr);
  if (state.subEl) {
    state.subEl.textContent = subtitle;
    state.subEl.style.display = subtitle ? "block" : "none";
  }

  const summary = buildSummary(vec);
  if (state.summaryEl) {
    state.summaryEl.textContent = summary;
    state.summaryEl.style.display = summary ? "block" : "none";
  }

  const labels = getLabelsFor(vectorKey, vec.length);
  drawBars(state.canvas, vec, labels);

  if (state.btnCsv) {
    state.btnCsv.onclick = async () => {
      const rows = ["bucket,label,value"];
      for (let i = 0; i < vec.length; i++) {
        const lab = labels?.[i] ?? String(i);
        const v = Number(vec[i]);
        const vv = Number.isFinite(v) ? String(v) : "";
        const sLab = String(lab);
        const qlab = sLab.includes(",") || sLab.includes('"') ? `"${sLab.replace(/"/g, '""')}"` : sLab;
        rows.push(`${i},${qlab},${vv}`);
      }
      const ok = await copyTextToClipboard(rows.join("\n"));
      flashIcon(state.btnCsv, ok);
    };
  }

  if (state.btnJson) {
    state.btnJson.onclick = async () => {
      const payload = { vectorKey, labels, values: vec };
      const ok = await copyTextToClipboard(JSON.stringify(payload, null, 2));
      flashIcon(state.btnJson, ok);
    };
  }

  state.anchor = anchorEl;
  state.tr = tr;
  state.lastVectorKey = vectorKey;
  state.lastColKey = clickedColKey;

  pop.open(anchorEl || null);
  try { requestAnimationFrame(() => pop.position?.()); } catch {}
}

// ---------------------------------------------------------
// Mark ONLY header cells for showOnCols
// ---------------------------------------------------------
function markPopoverHeaders(tableEl, cfg) {
  if (!tableEl) return;

  // Always clear old marks
  tableEl.querySelectorAll("thead th[data-te-has-popover-col='1']").forEach((th) => {
    delete th.dataset.teHasPopoverCol;
  });

  const keys = Array.isArray(cfg?.showOnCols) ? cfg.showOnCols.map(String) : [];
  if (!keys.length) return;

  keys.forEach((colKey) => {
    const th = tableEl.querySelector(`thead th[data-col-key="${CSS.escape(colKey)}"]`);
    if (!th) return;
    th.dataset.teHasPopoverCol = "1";
    ensureHeaderLabelSpan(th);
  });
}

function installOnTable(tableEl, cfg) {
  if (!tableEl) return () => {};
  if (tableEl.__teBucketsInstalled && typeof tableEl.__teBucketsCleanup === "function") {
    return tableEl.__teBucketsCleanup;
  }

  tableEl.__teBucketsInstalled = true;

  const showOn = Array.isArray(cfg?.showOnCols) ? new Set(cfg.showOnCols.map(String)) : null;
  const vmap = cfg?.vectorKeyByCol && typeof cfg.vectorKeyByCol === "object" ? cfg.vectorKeyByCol : {};

  const onRender = () => markPopoverHeaders(tableEl, cfg);
  const onClick = (e) => {
    const td = e.target?.closest?.("tbody td");
    if (!td) return;

    const tr = td.closest?.("tr");
    if (!tr) return;

    const colKey = String(getColKeyFromCell(td) || "");
    if (!colKey) return;

    if (showOn && !showOn.has(colKey)) return;

    const vectorKey = String(vmap[colKey] || colKey);
    const vec = getVectorFor(tr, vectorKey);
    if (!vec) return;

    if (state.pop?.isOpen?.() && state.anchor === td && state.lastVectorKey === vectorKey) {
      closePopover();
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    openPopover(td, tr, vectorKey, colKey);
    e.preventDefault();
    e.stopPropagation();
  };

  markPopoverHeaders(tableEl, cfg);
  tableEl.addEventListener("te:render", onRender);
  tableEl.addEventListener("click", onClick, true);

  let cleaned = false;
  const dispose = () => {
    if (cleaned) return;
    cleaned = true;

    try {
      tableEl.removeEventListener("te:render", onRender);
    } catch {}
    try {
      tableEl.removeEventListener("click", onClick, true);
    } catch {}

    tableEl.querySelectorAll("thead th[data-te-has-popover-col='1']").forEach((th) => {
      delete th.dataset.teHasPopoverCol;
    });

    if (state.anchor && tableEl.contains(state.anchor)) {
      closePopover();
    }

    try {
      delete tableEl.__teBucketsInstalled;
    } catch {
      tableEl.__teBucketsInstalled = false;
    }

    try {
      delete tableEl.__teBucketsCleanup;
    } catch {
      tableEl.__teBucketsCleanup = null;
    }
  };

  tableEl.__teBucketsCleanup = dispose;
  return dispose;
}

export const TableEngineBucketPopoverPlugin = {
  name: "BucketPopoverPlugin",
  init(engine, ctx) {
    const spec = ctx?.spec || {};
    const cfg = spec.bucketPopover || null;

    if (!cfg || (!cfg.showOnCols && !cfg.vectorKeyByCol)) return null;

    const tableId = spec?.tableId || spec?.id || engine?.tableId || null;
    if (!tableId) return null;

    const table = document.getElementById(tableId);
    if (!table) return null;

    return installOnTable(table, cfg);
  },
};

export function registerBucketPopoverPlugin() {
  const TE = window.TableEngine;
  if (!TE?.registerPlugin) return false;
  TE.registerPlugin(TableEngineBucketPopoverPlugin);
  return true;
}

export default TableEngineBucketPopoverPlugin;
